import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useFeedStore } from './feed';
import { useFollowStore } from './follows';
import { useMessageStore } from './messages';
import { useOfferStore } from './offers';
import { useClubStore } from './clubs';
import { useBlockStore } from './blocks';
import { useNotificationStore } from '@/hooks/use-notification-store';
import { useSession } from './session';
import { getUserById } from './users';

type FeedStore = ReturnType<typeof useFeedStore>;
type FollowStore = ReturnType<typeof useFollowStore>;
type MessageStore = ReturnType<typeof useMessageStore>;
type OfferStore = ReturnType<typeof useOfferStore>;
type ClubStore = ReturnType<typeof useClubStore>;
type NotificationStore = ReturnType<typeof useNotificationStore>;
type BlockStore = ReturnType<typeof useBlockStore>;

interface AppStore {
  feed: FeedStore;
  follow: FollowStore;
  message: MessageStore;
  offer: OfferStore;
  club: ClubStore;
  notification: NotificationStore;
  block: BlockStore;
}

const APP_STORE_CTX_KEY = '__APP_STORE_CONTEXT__';
const AppStoreContext: React.Context<AppStore | null> =
  (globalThis as any)[APP_STORE_CTX_KEY] ??
  ((globalThis as any)[APP_STORE_CTX_KEY] = createContext<AppStore | null>(null));

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const { currentUserId } = useSession();
  const uid = currentUserId ?? '__none__';

  const feed = useFeedStore(uid);
  const follow = useFollowStore(uid);
  const currentUser = getUserById(uid);
  const message = useMessageStore(uid, currentUser?.plan ?? 'free');
  const offer = useOfferStore(uid);
  const club = useClubStore(uid);
  const block = useBlockStore(uid);
  const notifStore = useNotificationStore(uid);


  const actorName = currentUser?.name ?? 'Someone';

  /**
   * Moderation filter (Apple Guideline 1.2): content authored by a blocked user
   * or by a banned account never reaches the UI — posts and comments alike.
   */
  const isMuted = useCallback((authorId: string) => {
    if (!authorId || authorId === uid) return false;
    if (block.blockedIds.has(authorId)) return true;
    return getUserById(authorId)?.banned === true;
  }, [block.blockedIds, uid]);

  const visiblePosts = useMemo(() => (
    feed.posts
      .filter((p) => !isMuted(p.authorId) && !(p.originalAuthorId && isMuted(p.originalAuthorId)))
      .map((p) => {
        const comments = p.comments.filter((c) => !isMuted(c.authorId));
        return comments.length === p.comments.length ? p : { ...p, comments };
      })
  ), [feed.posts, isMuted]);

  // Wrap feed actions to emit notifications
  const wrappedFeed = useMemo<FeedStore>(() => ({
    ...feed,
    posts: visiblePosts,

    toggleLike: (postId: string) => {
      const post = feed.posts.find((p) => p.id === postId);
      const wasLiked = post?.likes.includes(uid);
      feed.toggleLike(postId);
      if (post && !wasLiked) {
        void notifStore.addNotification({
          userId: post.authorId,
          actorId: uid,
          type: 'like',
          targetId: postId,
          targetType: 'post',
          message: `${actorName} liked your post`,
        });
      }
    },
    addComment: (postId: string, text: string, parentCommentId?: string, mentions?: import('./feed').CommentMention[]) => {
      const post = feed.posts.find((p) => p.id === postId);
      const comment = feed.addComment(postId, text, parentCommentId, mentions)!;
      if (post && text.trim() && comment) {
        if (parentCommentId) {
          const parentComment = post.comments.find((c) => c.id === parentCommentId);
          if (parentComment) {
            void notifStore.addNotification({
              userId: parentComment.authorId,
              actorId: uid,
              type: 'comment',
              targetId: postId,
              targetType: 'post',
              message: `${actorName} replied to your comment`,
            });
          }
        } else {
          void notifStore.addNotification({
            userId: post.authorId,
            actorId: uid,
            type: 'comment',
            targetId: postId,
            targetType: 'post',
            message: `${actorName} commented on your post`,
          });
        }
        // Mention notifications
        if (mentions?.length) {
          mentions.forEach((m) => {
            if (m.userId !== uid) {
              void notifStore.addNotification({
                userId: m.userId,
                actorId: uid,
                type: 'comment',
                targetId: postId,
                targetType: 'post',
                message: `${actorName} mentioned you in a comment`,
              });
            }
          });
        }
      }
      return comment;
    },
    toggleCommentLike: (postId: string, commentId: string) => {
      const post = feed.posts.find((p) => p.id === postId);
      const comment = post?.comments.find((c) => c.id === commentId);
      const wasLiked = comment?.likes.includes(uid);
      feed.toggleCommentLike(postId, commentId);
      if (comment && !wasLiked) {
        void notifStore.addNotification({
          userId: comment.authorId,
          actorId: uid,
          type: 'like',
          targetId: postId,
          targetType: 'post',
          message: `${actorName} liked your comment`,
        });
      }
    },
    repost: (postId: string) => {
      const post = feed.posts.find((p) => p.id === postId);
      const wasReposted = post?.repostedBy.includes(uid);
      feed.repost(postId);
      if (post && !wasReposted) {
        void notifStore.addNotification({
          userId: post.authorId,
          actorId: uid,
          type: 'repost',
          targetId: postId,
          targetType: 'post',
          message: `${actorName} reposted your post`,
        });
      }
    },
    createPost: async (data: Parameters<typeof feed.createPost>[0]) => {
      const postId = await feed.createPost(data);
      if (data.taggedUserIds) {
        data.taggedUserIds.forEach((taggedId) => {
          void notifStore.addNotification({
            userId: taggedId,
            actorId: uid,
            type: 'tag',
            targetId: postId,
            targetType: 'post',
            message: `${actorName} tagged you in a post`,
          });
        });
      }
      return postId;
    },
    editCaption: (postId: string, newContent: string, taggedUserIds?: string[]) => {
      const post = feed.posts.find((p) => p.id === postId);
      const oldTags = post?.taggedUserIds ?? [];
      feed.editCaption(postId, newContent, taggedUserIds);
      // Notify newly tagged users
      const newTags = taggedUserIds ?? [];
      newTags.filter((id) => !oldTags.includes(id)).forEach((taggedId) => {
        void notifStore.addNotification({
          userId: taggedId,
          actorId: uid,
          type: 'tag',
          targetId: postId,
          targetType: 'post',
          message: `${actorName} tagged you in a post`,
        });
      });
    },
  }), [feed, visiblePosts, uid, actorName, notifStore]);

  // Wrap follow
  const wrappedFollow = useMemo<FollowStore>(() => ({
    ...follow,
    toggleFollow: (targetId: string) => {
      const wasFollowing = follow.isFollowing(targetId);
      follow.toggleFollow(targetId);
      if (!wasFollowing) {
        void notifStore.addNotification({
          userId: targetId,
          actorId: uid,
          type: 'follow',
          targetId: uid,
          targetType: 'profile',
          message: `${actorName} started following you`,
        });
      }
    },
  }), [follow, uid, actorName, notifStore]);

  // Wrap offer actions
  const wrappedOffer = useMemo<OfferStore>(() => ({
    ...offer,
    applyToOffer: async (offerId: string, extra?: { message?: string; documentUrl?: string }) => {
      const o = offer.offers.find((x) => x.id === offerId);
      await offer.applyToOffer(offerId, extra);
      if (o) {
        void notifStore.addNotification({
          userId: o.createdById,
          actorId: uid,
          type: 'offer_application',
          targetId: offerId,
          targetType: 'offer',
          message: `${actorName} applied to "${o.title}"`,
        });
      }
    },
    updateApplicationStatus: async (appId: string, status: 'accepted' | 'rejected') => {
      const app = offer.getApplicationById(appId);
      await offer.updateApplicationStatus(appId, status);
      if (app) {
        void notifStore.addNotification({
          userId: app.applicantId,
          actorId: uid,
          type: status === 'accepted' ? 'application_accepted' : 'application_rejected',
          targetId: app.offerId,
          targetType: 'offer',
          message: status === 'accepted'
            ? `${actorName} accepted your application`
            : `${actorName} rejected your application`,
        });
      }
    },

  }), [offer, uid, actorName, notifStore]);

  // Wrap message actions
  const wrappedMessage = useMemo(() => ({
    ...message,
    sendMessage: async (
      threadId: string,
      text: string,
      sharedPost?: import('@/types/message').SharedPostAttachment,
      media?: { blob: Blob; type: 'image' | 'video' },
    ) => {
      await message.sendMessage(threadId, text, sharedPost, media);
      if (!text.trim() && !sharedPost && !media) return;
      const thread = message.getThread(threadId);
      if (thread) {
        const recipients = thread.participants.filter((p) => p !== uid);
        recipients.forEach((recipientId) => {
          void notifStore.addNotification({
            userId: recipientId,
            actorId: uid,
            type: 'message',
            targetId: threadId,
            targetType: 'thread',
            message: sharedPost
              ? `${actorName} shared a post with you`
              : `${actorName} sent you a message`,
          });
        });
      }
    },
    addMemberToGroup: async (threadId: string, userId: string) => {
      await message.addMemberToGroup(threadId, userId);
      void notifStore.addNotification({
        userId,
        actorId: uid,
        type: 'message',
        targetId: threadId,
        targetType: 'thread',
        message: `${actorName} added you to a group conversation`,
      });
    },
  }), [message, uid, actorName, notifStore]);


  // Wrap club invitation actions
  const wrappedClub = useMemo<ClubStore>(() => ({
    ...club,
    invitePlayer: async (clubId: string, teamId: string, playerId: string) => {
      await club.invitePlayer(clubId, teamId, playerId);
      const c = club.getClub(clubId);
      void notifStore.addNotification({
        userId: playerId,
        actorId: uid,
        type: 'club_invitation',
        targetId: clubId,
        targetType: 'club',
        message: `${actorName} invited you to ${c?.name ?? 'a club'}`,
      });
    },
    inviteStaff: async (clubId: string, userId: string, role: any, teamId?: string) => {
      await club.inviteStaff(clubId, userId, role, teamId);
      const c = club.getClub(clubId);
      void notifStore.addNotification({
        userId,
        actorId: uid,
        type: 'club_invitation',
        targetId: clubId,
        targetType: 'club',
        message: `${actorName} invited you as ${role} at ${c?.name ?? 'a club'}`,
      });
    },
    respondToInvitation: async (invitationId: string, accept: boolean) => {
      // Find the invitation before responding to get the inviter info
      const inv = club.getInvitationsForUser(uid).find((i) => i.id === invitationId) ??
        club.getInvitationsForUser(uid).find((i) => i.id === invitationId);
      await club.respondToInvitation(invitationId, accept);
      if (inv) {
        const c = club.getClub(inv.clubId);
        void notifStore.addNotification({
          userId: c?.ownerId ?? '',
          actorId: uid,
          type: accept ? 'invitation_accepted' : 'invitation_rejected',
          targetId: inv.clubId,
          targetType: 'club',
          message: accept
            ? `${actorName} accepted the invitation to ${c?.name ?? 'your club'}`
            : `${actorName} declined the invitation to ${c?.name ?? 'your club'}`,
        });
      }
    },
  }), [club, uid, actorName, notifStore]);

  const store: AppStore = {
    feed: wrappedFeed,
    follow: wrappedFollow,
    message: wrappedMessage,
    offer: wrappedOffer,
    club: wrappedClub,
    block,

    notification: notifStore,
  };

  return (
    <AppStoreContext.Provider value={store}>
      {children}
    </AppStoreContext.Provider>
  );
}

export function useAppStore(): AppStore {
  const ctx = useContext(AppStoreContext);
  if (!ctx) throw new Error('useAppStore must be used within AppStoreProvider');
  return ctx;
}

/**
 * Same as useAppStore, but returns null instead of throwing when rendered
 * outside the provider (e.g. shared chrome mounted on onboarding screens).
 */
export function useAppStoreOptional(): AppStore | null {
  return useContext(AppStoreContext);
}

